import { useState, useEffect, useCallback, useMemo } from 'react'
import { useAuth } from '@/modules/auth'
import { useTripRouteSchedule } from '@/modules/routes/hooks/useTripRouteSchedule'
import { useTripWaypoints } from '@/modules/trips/hooks/useTripWaypoints'
import { mergeTripWaypoints } from '@/modules/trips/utils/mergeWaypoints'

/**
 * Unified hook for fetching trip overview data.
 * Fetches trip waypoints and route schedule in parallel and returns combined state.
 * 
 * @param tripId - The trip ID to fetch data for
 * @returns Combined loading, error states, and merged waypoints
 */
export function useTripOverviewData(tripId: string) {
  const { waypoints: tripWaypoints, loading: isWaypointsLoading, error: waypointsError, fetchWaypoints, updateWaypoint, updateWaypointInState, reorderWaypoints, deleteWaypoint } = useTripWaypoints({ tripId })
  
  const {
    schedule: routeSchedule,
    loading: isRouteScheduleLoading,
    error: routeScheduleError,
    refresh: refreshRouteSchedule,
    updateWaypointInSchedule
  } = useTripRouteSchedule(tripId)

  // Combine loading states
  const isLoading = isWaypointsLoading || isRouteScheduleLoading
  
  // Combine error states (prioritize route schedule error as it's more critical)
  const error = routeScheduleError || waypointsError || null

  // Merge waypoints from both sources
  const allWaypoints = useMemo(() => {
    const merged = mergeTripWaypoints(tripWaypoints, routeSchedule)
    return merged
  }, [tripWaypoints, routeSchedule, tripId])

  // Refresh both data sources (pass cache: 'no-store' after mutations to avoid stale cache)
  const refresh = useCallback(async (options?: { cache?: RequestCache }) => {
    await Promise.all([
      fetchWaypoints(options),
      refreshRouteSchedule(tripId)
    ])
  }, [fetchWaypoints, refreshRouteSchedule, tripId])

  return {
    waypoints: allWaypoints,
    tripWaypoints,
    routeSchedule,
    isLoading,
    error,
    refresh,
    fetchWaypoints,
    updateWaypoint,
    updateWaypointInState,
    refreshRouteSchedule,
    updateWaypointInSchedule,
    reorderWaypoints,
    deleteWaypoint
  }
}

